/**
 * Auth Store - Zustand State Management
 * 
 * Manages user authentication state.
 * 
 * @validates Requirements 3.1, 3.2, 3.3, 3.6 - Authentication flow
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, UserRole } from '@budgira/shared';
import { authService, LoginResponse, getToken, clearTokens } from '../services/auth.service';
import { useAccountStore } from './accountStore';
import { useOperationStore } from './operationStore';
import { useSchedulerStore } from './schedulerStore';
import { useUserSettingsStore } from './userSettingsStore';
import { useProjectStore } from './projectStore';

// Storage type key to track which storage is being used (same as auth.service.ts)
const AUTH_STORAGE_TYPE_KEY = 'budgira_auth_type';

/**
 * Get the appropriate storage based on remember me preference
 */
function getAuthStorage(): Storage {
  const storageType = localStorage.getItem(AUTH_STORAGE_TYPE_KEY);
  return storageType === 'local' ? localStorage : sessionStorage;
}

/**
 * Custom storage that uses localStorage or sessionStorage based on remember me preference
 */
const authStorage = {
  getItem: (name: string): string | null => {
    // Check both storages - localStorage first (remember me), then sessionStorage
    let value = localStorage.getItem(name);
    if (value) return value;
    value = sessionStorage.getItem(name);
    return value;
  },
  setItem: (name: string, value: string): void => {
    // Use the same storage as the tokens
    const storage = getAuthStorage();
    // Clear both to avoid conflicts
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
    storage.setItem(name, value);
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
};

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  requiresTwoFactor: boolean;
  requiresEmail2FA: boolean;
  userId: string | null;
  email2FASessionId: string | null;
  rememberMe: boolean;
  
  // Actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>;
  verify2FA: (code: string) => Promise<boolean>;
  verifyEmail2FA: (code: string) => Promise<{ success: boolean; error?: string; attemptsRemaining?: number; isLocked?: boolean }>;
  resendEmail2FA: () => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  register: (email: string, password: string, language?: 'fr' | 'en') => Promise<boolean>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  resetEmail2FAState: () => void;
  refreshUser: () => Promise<User | null>;
  
  // Computed
  isPremium: () => boolean;
  isAdmin: () => boolean;
  isSecondaryUser: () => boolean;
  canManageCategories: () => boolean;
  canManagePaymentMethods: () => boolean;
  canManageSchedulers: () => boolean;
  canManageSharedAccess: () => boolean;
  canUseReconciliation: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      requiresTwoFactor: false,
      requiresEmail2FA: false,
      userId: null,
      email2FASessionId: null,
      rememberMe: false,

      login: async (email: string, password: string, rememberMe: boolean = false): Promise<boolean> => {
        set({ isLoading: true, error: null, rememberMe });
        try {
          const response = await authService.login({ email, password, rememberMe });
          
          if (!response.success) {
            set({ error: response.error?.message || 'Login failed', isLoading: false });
            return false;
          }

          const data = response.data as LoginResponse;
          
          // Check for TOTP 2FA requirement (backend uses requires2FA)
          if (data.requires2FA) {
            set({
              requiresTwoFactor: true,
              userId: data.user?.id || null,
              isLoading: false,
            });
            return true;
          }

          // Check for email 2FA requirement
          if (data.requiresEmail2FA && data.userId && data.sessionId) {
            set({
              requiresEmail2FA: true,
              userId: data.userId,
              email2FASessionId: data.sessionId,
              isLoading: false,
            });
            return true;
          }

          // Reset pagination when logging in
          useOperationStore.getState().setPage(1);
          useSchedulerStore.getState().setPage(1);
          
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            requiresTwoFactor: false,
            requiresEmail2FA: false,
            userId: null,
            email2FASessionId: null,
          });
          return true;
        } catch (err) {
          // Handle axios errors with API response
          let message = 'An error occurred';
          if (err && typeof err === 'object' && 'response' in err) {
            const axiosError = err as { response?: { data?: { error?: { message?: string; code?: string } } } };
            message = axiosError.response?.data?.error?.message || message;
          } else if (err instanceof Error) {
            message = err.message;
          }
          set({ error: message, isLoading: false });
          return false;
        }
      },

      verify2FA: async (code: string): Promise<boolean> => {
        const { userId, rememberMe } = get();
        if (!userId) {
          set({ error: 'User ID not found for 2FA' });
          return false;
        }

        set({ isLoading: true, error: null });
        try {
          const response = await authService.verify2FA(code, userId, rememberMe);
          
          if (!response.success) {
            set({ error: response.error?.message || '2FA verification failed', isLoading: false });
            return false;
          }

          const data = response.data as LoginResponse;
          
          // Reset pagination when logging in
          useOperationStore.getState().setPage(1);
          useSchedulerStore.getState().setPage(1);
          
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            requiresTwoFactor: false,
            userId: null,
          });
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ error: message, isLoading: false });
          return false;
        }
      },

      verifyEmail2FA: async (code: string): Promise<{ success: boolean; error?: string; attemptsRemaining?: number; isLocked?: boolean }> => {
        const { userId, email2FASessionId, rememberMe } = get();
        if (!userId || !email2FASessionId) {
          return { success: false, error: 'Session not found' };
        }

        set({ isLoading: true, error: null });
        try {
          const response = await authService.verifyEmail2FA(userId, email2FASessionId, code, rememberMe);
          
          if (!response.success) {
            const errorData = response.error as { code?: string; message?: string; attemptsRemaining?: number };
            const isLocked = errorData?.code === 'ACCOUNT_LOCKED';
            set({ isLoading: false });
            return { 
              success: false, 
              error: errorData?.message || 'Verification failed',
              attemptsRemaining: errorData?.attemptsRemaining,
              isLocked,
            };
          }

          const data = response.data as LoginResponse;
          
          // Reset pagination when logging in
          useOperationStore.getState().setPage(1);
          useSchedulerStore.getState().setPage(1);
          
          set({
            user: data.user,
            isAuthenticated: true,
            isLoading: false,
            requiresEmail2FA: false,
            userId: null,
            email2FASessionId: null,
          });
          return { success: true };
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ isLoading: false });
          return { success: false, error: message };
        }
      },

      resendEmail2FA: async (): Promise<{ success: boolean; error?: string }> => {
        const { userId, email2FASessionId } = get();
        if (!userId || !email2FASessionId) {
          return { success: false, error: 'Session not found' };
        }

        try {
          const response = await authService.resendEmail2FA(userId, email2FASessionId);
          
          if (!response.success) {
            return { 
              success: false, 
              error: response.error?.message || 'Failed to resend code',
            };
          }

          return { success: true };
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          return { success: false, error: message };
        }
      },

      logout: async (): Promise<void> => {
        try {
          await authService.logout();
        } finally {
          // Reset all related stores
          useAccountStore.getState().reset();
          useOperationStore.getState().setPage(1);
          useSchedulerStore.getState().setPage(1);
          useUserSettingsStore.getState().reset();
          useProjectStore.getState().resetStore();
          
          // Force light mode on logout
          document.documentElement.classList.remove('dark');
          
          // Clear tokens (already done in authService.logout but ensure it's done)
          clearTokens();
          
          set({
            user: null,
            isAuthenticated: false,
            requiresTwoFactor: false,
            requiresEmail2FA: false,
            userId: null,
            email2FASessionId: null,
            rememberMe: false,
            error: null,
          });
        }
      },

      register: async (email: string, password: string, language: 'fr' | 'en' = 'fr'): Promise<boolean> => {
        set({ isLoading: true, error: null });
        try {
          const response = await authService.register({ email, password, language });
          
          if (!response.success) {
            set({ error: response.error?.message || 'Registration failed', isLoading: false });
            return false;
          }

          set({ isLoading: false });
          return true;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'An error occurred';
          set({ error: message, isLoading: false });
          return false;
        }
      },

      checkAuth: async (): Promise<void> => {
        const token = getToken();
        
        if (!token) {
          // No token means we can't authenticate - reset state
          set({ isAuthenticated: false, user: null, isLoading: false });
          return;
        }

        // We have a token, validate it with the server
        set({ isLoading: true });
        try {
          const response = await authService.getCurrentUser();
          if (response.success && response.data) {
            set({
              user: response.data,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            // Token exists but validation failed - token is invalid, clear it
            clearTokens();
            set({ isAuthenticated: false, user: null, isLoading: false });
          }
        } catch {
          // Network error - token might still be valid, keep current state if authenticated
          const currentState = get();
          if (currentState.isAuthenticated && currentState.user) {
            // Keep authenticated state on network error (offline support)
            set({ isLoading: false });
          } else {
            // No previous auth state, reset
            set({ isAuthenticated: false, user: null, isLoading: false });
          }
        }
      },

      clearError: () => set({ error: null }),

      resetEmail2FAState: () => set({ 
        requiresEmail2FA: false, 
        userId: null, 
        email2FASessionId: null,
        error: null,
      }),

      refreshUser: async (): Promise<User | null> => {
        try {
          const response = await authService.getCurrentUser();
          if (response.success && response.data) {
            set({
              user: response.data,
              isAuthenticated: true,
            });
            return response.data;
          }
          return null;
        } catch {
          return null;
        }
      },

      isPremium: () => {
        const { user } = get();
        // For secondary users, use the effective role from the owner
        if (user?.sharedAccess?.effectiveRole) {
          return user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
        }
        return user?.role === UserRole.PREMIUM || user?.role === UserRole.ADMIN;
      },

      isAdmin: () => {
        const { user } = get();
        // Secondary users are never admins themselves, even if owner is admin
        if (user?.sharedAccess) {
          return false;
        }
        return user?.role === UserRole.ADMIN;
      },

      isSecondaryUser: () => {
        const { user } = get();
        return user?.sharedAccess !== null && user?.sharedAccess !== undefined;
      },

      canManageCategories: () => {
        const { user } = get();
        // If user not loaded yet, allow (auth check will handle redirect)
        if (!user) return true;
        // Secondary users need permission from shared access
        if (user.sharedAccess) {
          return user.sharedAccess.canManageCategories === true;
        }
        // Free primary users cannot manage categories
        if (user.role === UserRole.FREE) return false;
        // Premium and admin primary users can manage
        return true;
      },

      canManagePaymentMethods: () => {
        const { user } = get();
        // If user not loaded yet, allow (auth check will handle redirect)
        if (!user) return true;
        // Secondary users need permission from shared access
        if (user.sharedAccess) {
          return user.sharedAccess.canManagePaymentMethods === true;
        }
        // Free primary users cannot manage payment methods
        if (user.role === UserRole.FREE) return false;
        // Premium and admin primary users can manage
        return true;
      },

      canManageSchedulers: () => {
        const { user } = get();
        // If user not loaded yet, allow (auth check will handle redirect)
        if (!user) return true;
        // Free users cannot manage schedulers (Premium feature)
        if (user.role === UserRole.FREE) return false;
        // Secondary users with premium effective role can access
        if (user.sharedAccess) {
          return user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
        }
        // Premium and admin primary users can manage
        return true;
      },

      canManageSharedAccess: () => {
        const { user } = get();
        // If user not loaded yet, allow (auth check will handle redirect)
        if (!user) return true;
        // Free users cannot manage shared access (Premium feature)
        if (user.role === UserRole.FREE) return false;
        // Secondary users cannot manage shared access (only primary users)
        if (user.sharedAccess) return false;
        // Premium and admin primary users can manage
        return true;
      },

      canUseReconciliation: () => {
        const { user } = get();
        // If user not loaded yet, allow (auth check will handle redirect)
        if (!user) return true;
        // Free users cannot use reconciliation (Premium feature)
        if (user.role === UserRole.FREE) return false;
        // Secondary users (shared access) need premium effective role AND at least one account with write access
        if (user.sharedAccess) {
          // Must have premium or admin effective role
          const hasPremiumRole = user.sharedAccess.effectiveRole === 'premium' || user.sharedAccess.effectiveRole === 'admin';
          if (!hasPremiumRole) return false;
          
          // Check if has write access on at least one account
          const accountPermissions = user.sharedAccess.accountPermissions;
          if (!accountPermissions || accountPermissions.length === 0) {
            // If no account permissions defined, fallback to checking effectiveRole only
            // This handles cases where accountPermissions might not be loaded
            return true;
          }
          const hasWriteAccess = accountPermissions.some(
            (perm) => perm.permission === 'write'
          );
          return hasWriteAccess;
        }
        // Premium and admin primary users can use
        return true;
      },
    }),
    {
      name: 'budgira-auth',
      storage: createJSONStorage(() => authStorage),
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        rememberMe: state.rememberMe,
      }),
      onRehydrateStorage: () => {
        return (_state, error) => {
          if (error) {
            console.error('Error rehydrating auth store:', error);
          }
        };
      },
    }
  )
);

// Helper to wait for hydration
export const waitForHydration = () => {
  return new Promise<void>((resolve) => {
    const unsubFinishHydration = useAuthStore.persist.onFinishHydration(() => {
      unsubFinishHydration();
      resolve();
    });
    
    // If already hydrated, resolve immediately
    if (useAuthStore.persist.hasHydrated()) {
      unsubFinishHydration();
      resolve();
    }
  });
};

export default useAuthStore;
